import { useMemo, useCallback, useState } from 'preact/hooks';

export type CollapsiblePressOptions = {
  isDisabled?: boolean;
};

const DefaultSettings = {
  isDisabled: false
};

/**
 * Returns a click handler that can make a target element either clickable or keyboard pressable.
 *
 * @param onPressHandler function
 * @param isDisabled boolean
 * @returns
 */
export function useCollapsiblePress(
  onPressHandler: (event: Event) => void,
  settings: CollapsiblePressOptions = DefaultSettings
): { pressProps: Record<string, any> } {
  const [isPressed, setIsPressed] = useState<boolean>(false);
  const mergedSettings = useMemo(() => {
    return { ...DefaultSettings, ...settings };
  }, [settings]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.code === 'Space' || event.code === 'Enter') {
        // event.preventDefault();
        setIsPressed(true);
      }
    },
    [setIsPressed]
  );

  const onKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (event.code === 'Space' || event.code === 'Enter') {
        setIsPressed(false);
        event.preventDefault();
        onPressHandler(event);
      }
    },
    [onPressHandler, setIsPressed]
  );

  const onBlur = useCallback(() => {
    setIsPressed(false);
  }, [setIsPressed]);

  const onClickSingle = useCallback(
    (event: Event) => {
      // See note above about why propagation is stopped: to prevent nested component bubbling clicks issue
      event.stopPropagation();
      if (!isPressed) {
        onPressHandler(event);
      }
    },
    [onPressHandler, isPressed]
  );

  const pressProps = mergedSettings.isDisabled
    ? {}
    : {
        onKeyDown,
        onKeyUp,
        onBlur,
        onClick: onClickSingle
      };

  return {
    pressProps
  };
}
